import React, { useCallback, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';
import { ButtonItem } from '@atlaskit/menu';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import showFlagMessages from '../../common/messages';
import type {
	SendMeATestEmailProps,
	SendMeATestEmailType,
} from '../../common/types/send-me-a-test-email';
import { useOutgoingEmailStateModel } from '../../controllers/outgoing-email-state';
import { useSendMeATestEmailService } from '../../services/send-me-a-test-email-service/index.tsx';
import messages from './messages';

export const SendMeATestEmailButton: SendMeATestEmailType = (props: SendMeATestEmailProps) => {
	const { data, loading, error, sendTestEmail } = useSendMeATestEmailService(
		props.projectKey,
		props.format,
		props.initialData,
		props.stylingData,
		props.ruleSetId,
	);

	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		actionSubject: 'sendMeATestEmail',
		action: 'clicked',
	});

	const { formatMessage } = useIntl();
	const outgoingEmailEnabled = useOutgoingEmailStateModel();

	const emailTemplatesPanelStateAnalyticsEvent = useMemo(
		() => createAnalyticsEvent({}),
		[createAnalyticsEvent],
	);
	useEffect(() => {
		fireOperationalAnalytics(emailTemplatesPanelStateAnalyticsEvent, 'outgoingEmailState fetched');
	}, [outgoingEmailEnabled.data, emailTemplatesPanelStateAnalyticsEvent]);

	const content = props.stylingData.textTemplate.replace(/\n/g, '').replace(/ /g, '');
	const isDisableSendMeTestEmail = !outgoingEmailEnabled.data?.enabled || content === '';
	const spinnerLoading = loading || outgoingEmailEnabled.loading;

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				title: showFlagMessages.sendTestEmailErrorFlag,
			});
		}
	}, [error, formatMessage, showFlag]);

	const sentTestEmailEvent = useMemo(() => createAnalyticsEvent({}), [createAnalyticsEvent]);
	useEffect(() => {
		if (data) {
			fireTrackAnalytics(sentTestEmailEvent, 'testEmail sent');
			showFlag({
				type: 'success',
				title: [showFlagMessages.sendTestEmailSuccessFlag, { email: data ? data.toAddress : '' }],
			});
		}
	}, [data, formatMessage, showFlag, sentTestEmailEvent]);

	useEffect(() => {
		if ((data || error) && props.setIsSendTestEmailDropDownOpen) {
			props.setIsSendTestEmailDropDownOpen(false);
		}
	}, [data, error, props]);

	const onClick = useCallback(
		(event: string) => {
			sendTestEmail();
			fireOperationalAnalytics(analyticsEvent, `${event} clicked`);
			outgoingEmailEnabled.refresh();
		},
		[analyticsEvent, outgoingEmailEnabled, sendTestEmail],
	);

	if (props.hasMultipleActions) {
		return (
			<ButtonWrapper>
				<ButtonItem
					testId="language-settings.ui.send-me-a-test-email.button-item-has-multiple-actions"
					onClick={() => onClick('multipleSendMeATestEmail')}
					isDisabled={isDisableSendMeTestEmail}
				>
					{formatMessage(messages.sendMeATestEmailButtonLabel)}
				</ButtonItem>
			</ButtonWrapper>
		);
	}

	if (props.shouldShowSendTestEmailButton) {
		return (
			<ButtonWrapper>
				<LoadingButton
					testId="language-settings.ui.send-me-a-test-email.button"
					onClick={() => onClick('singleSendMeATestEmail')}
					isDisabled={isDisableSendMeTestEmail}
					isLoading={spinnerLoading}
				>
					{formatMessage(messages.sendMeATestEmailButtonLabel)}
				</LoadingButton>
			</ButtonWrapper>
		);
	}

	return (
		<ButtonWrapper>
			<ButtonItem
				testId="language-settings.ui.send-me-a-test-email.button-item"
				onClick={() => onClick('buttonItemSendMeATestEmail')}
				isDisabled={loading}
			>
				{props.format === 'html'
					? formatMessage(messages.htmlEmailLabel)
					: formatMessage(messages.plainTextEmailLabel)}
			</ButtonItem>
		</ButtonWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	float: 'left',
});
